import store from '../../store';
import router from '@/router';
import VueInstance from '@/main';
import { v4 as uuidv4 } from 'uuid';
import { METHOD, RESPONSE_MESSAGE_KEY } from '@/constants/api';
import {
  LOCAL_STORAGE_KEY,
  useLocalStorage,
} from '@/composables/use-local-storage';
import { upperCaseToKebabCase } from '@/util/text';
import {
  getUserContextForSentry,
  addUserToSentryScope,
} from '@/util/sentry-user';
import { isUuid } from '@/util/uuid';

const formatValidationMessage = (message) => {
  let normalizedMessages = [];

  if (Array.isArray(message)) {
    normalizedMessages = message;
  } else if (message) {
    normalizedMessages = [message];
  }

  return normalizedMessages
    .map((singleMessage) => singleMessage)
    .filter(Boolean)
    .join(', ');
};

const maybeLogInvalidBrandUrl = (config, storedUser) => {
  try {
    const url = config?.url || '';
    if (!url.includes('/brands/')) return;

    // Extract `/brands/:brandId` (first segment after `/brands/`)
    const match = url.match(/\/brands\/([^/?#]+)/);
    const brandIdFromPath = match?.[1];

    if (!brandIdFromPath) return;
    if (isUuid(brandIdFromPath)) return;

    if (process.env.VUE_APP_SENTRY_DSN) {
      const Sentry = require('@sentry/vue');

      Sentry.withScope((scope) => {
        const userContext = getUserContextForSentry(storedUser);
        if (userContext) {
          addUserToSentryScope(scope, userContext);
        }

        scope.setLevel('warning');
        scope.setTag('module', 'api');
        scope.setTag('action', 'requestInterceptor');
        scope.setTag('errorType', 'invalid_brand_in_url');
        scope.setTag('url', url);

        scope.setExtra('brandUrl', {
          url,
          method: config?.method,
          brandIdFromPath,
          requestId: config?.headers?.['request-id'],
          clientId: config?.headers?.['client-id'],
          currentUrl:
            typeof window !== 'undefined' ? window.location.href : null,
          route: {
            name: router.currentRoute?.name,
            path: router.currentRoute?.path,
            fullPath: router.currentRoute?.fullPath,
            params: router.currentRoute?.params,
            query: router.currentRoute?.query,
          },
          storeUser: {
            id: storedUser?.id,
            email: storedUser?.email,
            role: storedUser?.role,
            type: storedUser?.type,
            systemRole: storedUser?.systemRole,
            brandId: storedUser?.brandId,
            brandObjId: storedUser?.brand?.id,
          },
        });

        Sentry.captureMessage(
          'Outgoing request contains invalid brand id in URL',
        );
      });
    }
  } catch (_) {
    // never break request flow
  }
};

const getBrowserClientId = () => {
  const { storedValue, setStoredValue } = useLocalStorage(
    LOCAL_STORAGE_KEY.CLIENT_ID,
  );
  let clientId = storedValue.value;

  if (!clientId) {
    clientId = uuidv4();

    setStoredValue(clientId);
  }

  return clientId;
};

export const requestInterceptor = (config) => {
  const { storedValue } = useLocalStorage(LOCAL_STORAGE_KEY.OBJEKTO);
  const storedUser = storedValue.value?.user;
  const storedViewer = storedUser.viewer;
  const blockAccess =
    storedViewer &&
    config.method !== METHOD.GET &&
    !config.url.includes('login');

  config.headers['request-id'] = uuidv4();
  config.headers['client-id'] = getBrowserClientId();
  config.withCredentials = true;
  if (storedViewer) {
    config.headers['viewer-id'] = storedViewer.id;
  }

  if (blockAccess) {
    VueInstance.$toast.info(VueInstance.$t('api.preview-mode-info'));
    return Promise.reject(new Error(RESPONSE_MESSAGE_KEY.ABORT));
  }

  // Diagnostic: catch `/brands/false` / `/brands/undefined` before it hits backend
  maybeLogInvalidBrandUrl(config, storedUser);

  return config;
};

export const responseErrorInterceptor = (err) => {
  let errMsg;

  if (err.message === RESPONSE_MESSAGE_KEY.ABORT) {
    return Promise.reject(err);
  }

  try {
    const { storedValue } = useLocalStorage(LOCAL_STORAGE_KEY.OBJEKTO);
    const userData = storedValue.value?.user;
    const userContext = getUserContextForSentry(userData);

    const errorContext = {
      url: err?.config?.url || 'unknown',
      method: err?.config?.method || 'unknown',
      errorType: err?.constructor?.name || 'unknown',
      hasResponse: !!err?.response,
      responseStatus: err?.response?.status,
      requestId: err?.config?.headers?.['request-id'],
      clientId: err?.config?.headers?.['client-id'],
    };

    // eslint-disable-next-line no-console
    console.error('API Error:', {
      ...errorContext,
      userId: userContext?.id,
      userEmail: userContext?.email,
    });

    if (process.env.VUE_APP_SENTRY_DSN) {
      const Sentry = require('@sentry/vue');

      Sentry.withScope((scope) => {
        if (userContext) {
          addUserToSentryScope(scope, userContext);
        }

        scope.setLevel('error');
        scope.setTag('type', err.response ? 'api_error' : 'api_no_response');
        scope.setTag('url', errorContext.url);
        scope.setTag('method', errorContext.method);
        scope.setTag('errorType', errorContext.errorType);

        if (errorContext.responseStatus) {
          scope.setTag('statusCode', String(errorContext.responseStatus));
        }

        scope.setContext('api_error', errorContext);

        // Diagnostic: explicit signal for UUID validation failures on brandId
        try {
          const msg = err?.response?.data?.message;
          const messages = Array.isArray(msg) ? msg : [msg];
          if (
            errorContext.url?.includes('/brands/') &&
            messages?.some?.(
              (m) =>
                typeof m === 'string' && m.includes('brandId must be a UUID'),
            )
          ) {
            scope.setTag('errorType', 'brand_uuid_validation');
            scope.setExtra('brand_uuid_validation', {
              url: errorContext.url,
              messages,
              requestId: errorContext.requestId,
              clientId: errorContext.clientId,
              currentUrl:
                typeof window !== 'undefined' ? window.location.href : null,
              route: {
                name: router.currentRoute?.name,
                path: router.currentRoute?.path,
                fullPath: router.currentRoute?.fullPath,
                params: router.currentRoute?.params,
                query: router.currentRoute?.query,
              },
            });
          }
        } catch (_) {
          // ignore
        }

        Sentry.captureException(err);
      });
    }
  } catch (loggingError) {
    // eslint-disable-next-line no-console
    console.error('Failed to log error:', loggingError);
  }

  if (!err.response) {
    VueInstance.$toast.error(VueInstance.$t('api.network-error'));
    return Promise.reject(err);
  }

  const status = err.response.status;
  const data = err.response.data;
  const config = err.response.config;
  const requestId = config.headers['request-id'];

  if ([403, 401].includes(status)) {
    if (!store.getters['user/isLogged']) {
      return Promise.reject(err);
    }

    store.commit('user/SET_VIEWER', null);
    store.commit('user/CLEAR_AUTH');
    router.push({ name: 'Login' });
    errMsg = VueInstance.$t('api.auth-error', { requestId });
  } else {
    errMsg = VueInstance.$t('api.general-error', { requestId });
    const { error, message, info } = data;

    if (error === 'USER_EMAIL_NOT_FOUND') {
      errMsg = VueInstance.$t(`api.${upperCaseToKebabCase(error)}`, info);
    } else if (error === 'USER_ROLE_NOT_SUPPORTED') {
      return Promise.reject(err);
    } else {
      switch (status) {
        case 400: {
          if (data.bodyResult?.issues) {
            const tooBigIssue = data.bodyResult.issues.find(
              (issue) =>
                issue.code === 'too_big' && issue.path?.includes('fileSize'),
            );
            if (tooBigIssue) {
              errMsg = VueInstance.$t('api.file-too-big-error', {
                requestId,
              });
              break;
            }
          }
          const validationMessage = formatValidationMessage(message);
          errMsg = `${VueInstance.$t('api.bad-parameters-error', {
            requestId,
          })}${validationMessage ? `: ${validationMessage}` : ''}`;
          break;
        }
        case 404:
          errMsg = VueInstance.$t('api.not-found', { requestId });
          break;
        case 409:
          errMsg = `${VueInstance.$t('api.conflict-error', {
            requestId,
          })}: ${message}`;
          break;
        case 422:
          errMsg = `${VueInstance.$t('api.unprocessable-entity', {
            requestId,
          })}: ${message}`;
      }
    }
  }
  if (config?.skipGlobalErrorToast) {
    return Promise.reject(err);
  }
  VueInstance.$toast.error(errMsg);
  return Promise.reject(err);
};
